import { CircularProgress } from '@material-ui/core';
import { TimeSeriesType, useQueryParamsData } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import clsx from 'clsx';
import React, { useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRitzTranslation } from '../../utils/customHooks';
import { SimpleSeriesMetricsDataProvider } from './SimpleSeriesMetricsDataProvider';
import { CollectionMetricsDataProvider } from './CollectionMetricsDataProvider';
import { useSelectTranslatedEntityClasses } from '..';

/**
 * This is a generic page that show historical data any 1 time series or 1 collection. This component does not actually display data.
 * It will check the URL param to see which type of data it needs to show and then use the appropriate component to show the data
 * based on the params.
 */
export const GenericDataViewPage: React.FC = () => {
    const params = useParams();
    const queryParams = useQueryParamsData();
    const projectId = Number(params.projectId);
    const homeId = params.homeId ? Number(params.homeId) : undefined;
    const deviceId = params.deviceId ? Number(params.deviceId) : undefined;
    const dataId = params.dataId as string;

    const { state:dataState } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, projectId);
    const { trans } = useRitzTranslation(project);
    const { dataDisplayName } = queryParams;
    const { tsdbSmartModuleId } = queryParams;

    const timeSeriesType: TimeSeriesType = queryParams.timeSeriesType as TimeSeriesType;


    // Get the 'metricsDefinition' info object from the query params if it exists.
    const metricsDefinitionFetchInfo = useMemo((): {
        readonly entityClass: string; readonly metricsDefinitionId: string;
    } | undefined => {
        if (queryParams.metricsDefinition) {
            try {
                // the 'metricsDefinition' is stringified so we need to parse it. Make sure it parse successfully and contains the required
                // data e.g.g entityClass and metricsDefinitionId
                const obj = JSON.parse(queryParams.metricsDefinition);
                if (typeof obj.entityClass === 'string' && typeof obj.metricsDefinitionId === 'string') {
                    return {
                        entityClass: obj.entityClass, metricsDefinitionId: obj.metricsDefinitionId,
                    };
                }
            } catch (error) {
                // Bad JSON. Ignore it.
            }
        }
        return undefined;
    }, [queryParams]);


    // Load all entity classes only if 'metricsDefinitionFetchInfo.entityClass' is provided. If entityClass is not provided, we still need
    // to call 'useSelectEntityClasses' because it is a HOOK which can't be called conditionally. We just pass 'undefined' for projectId
    // to make 'useSelectEntityClasses' not load anything.
    const entityClasses = useSelectTranslatedEntityClasses(projectId, trans);


    // If 'metricsDefinition' is provided in the URL, try to find the metricsDefinitions based on the 'metricsDefinition'
    const metricsDefinition = useMemo(() => {
        if (entityClasses && metricsDefinitionFetchInfo) {
            // First, find the entityClass that that matches the metricsDefinitionFetchInfo.entityClass
            const entityClass = entityClasses.find((entityClass) => {
                return entityClass.entityClass === metricsDefinitionFetchInfo.entityClass;
            });

            if (entityClass) {
                // Then find the metricsDefinition from the entityClass that match the metricsDefinitionFetchInfo.metricsDefinitionId
                return entityClass.metricsDefinitions?.find((metricDef) => {
                    return metricDef.metricsDefinitionId === metricsDefinitionFetchInfo.metricsDefinitionId;
                });
            }
        }
        return undefined;
    }, [entityClasses, metricsDefinitionFetchInfo]);


    // The child's action that needs to be shown in the page's title bar as the main action.
    // This is a way for the page to show a UI component created and controlled by a child components
    const [pageMainAction, setPageMainAction] = useState<JSX.Element | undefined>();


    // Split the dataIds into an array of timeSeriesIds and cache it
    const timeSeriesIds = useMemo(() => {
        if (tsdbSmartModuleId && timeSeriesType === TimeSeriesType.SIMPLE) {
            return dataId.split(',').map((seriesId: string) => {
                return {
                    seriesId,
                };
            });
        }
        return [];
    }, [dataId, timeSeriesType, tsdbSmartModuleId]);


    return (
        <div className={clsx('page-main-content')}>
            <div className="title-bar">
                <div className="title-bar-item title-text">
                    <div className="page-title">
                        {trans('page.title.historical_data_view')}
                    </div>
                    <div className="page-subtitle">
                        {dataDisplayName || trans('page.subtitle.historical_data_view')}
                    </div>
                </div>
                {pageMainAction}
            </div>

            {tsdbSmartModuleId && timeSeriesType === TimeSeriesType.COLLECTION && (
                <>
                    <CollectionMetricsDataProvider
                        projectId={projectId}
                        deviceId={deviceId}
                        collectionId={dataId}
                        collectionDisplayName={dataDisplayName}
                        homeId={homeId}
                        tsdbSmartModuleId={tsdbSmartModuleId}
                        numericMetrics={metricsDefinition?.numericMetrics}
                        tagMetrics={metricsDefinition?.tagMetrics}
                        setPageMainAction={setPageMainAction}
                    />
                </>
            )}

            {tsdbSmartModuleId && timeSeriesType === TimeSeriesType.SIMPLE && (
                <SimpleSeriesMetricsDataProvider
                    projectId={projectId}
                    deviceId={deviceId}
                    timeSeriesIds={timeSeriesIds}
                    timeSeriesDisplayName={dataDisplayName}
                    homeId={homeId}
                    tsdbSmartModuleId={tsdbSmartModuleId}
                    setPageMainAction={setPageMainAction}
                />
            )}

            {!tsdbSmartModuleId && (
                <CircularProgress />
            )}
        </div>
    );
};
